import { createContext, useEffect, useMemo, useRef, useState } from 'react'
import { Stack, useTheme } from '@mui/material'
import { CategoryType, Message } from '@weblite/ts-action/message'
import { useShowAudioPlayer } from '@weblite/audio'
import { closeCurrentModal } from 'weblite-web-modal-viewer'
import { useHistoryCustomBack, useNavigate } from 'weblite-router'
import { ITab, Tabs } from '$common/tabs/tabs'
import { AudioPlayer } from '$common/audio-player'
import { PageWithAppBar } from '$common/page-with-app-bar'
import { useMultiSelectList } from '$common/multi-select-searchable-list-page'
import { AUDIO_PLAYER_HEIGHT } from '$common/audio-player/audio-player-constants'
import { showSharedMediaLoadingModal } from '$comp/modals/loading-modal'
import { useOpenedRoomSharedMedia } from '$logic/shared-media/react-hooks'
import { useRoom } from '$logic/room/react-hook'
import { closeSharedMedia } from '$logic/router'
import {
  initiateSharedMediaContents,
  loadMoreSharedMediaContents,
} from '$logic/shared-media/service'
import { openedRoomSharedMediaStorage } from '$logic/shared-media/opened-room-shared-media.storage'
import { AppBar } from './components/app-bar'
import { SelectionBar } from './components/selection-bar'
import { MediaList } from './components/media-list'
import { EmptyState } from './components/empty-state'
import { t } from '$i18n'
import { SharedMediaCategoryType } from '$logic/shared-media/types'
import {
  createUserWaitReporter,
  UserWaitPendingAction,
} from '$logic/analytics/reporters/user-wait'

const CATEGORIES: SharedMediaCategoryType[] = [
  'MEDIA',
  'FILE',
  'LINK',
  'VOICE',
  'AUDIO',
]

const APPBAR_HEIGHT = 50
const TABS_HEIGHT = 45

function a11yProps(index: number) {
  return {
    key: `shared-media-tab-${index}`,
    'aria-controls': `shared-media-tab-panel-${index}`,
  }
}

export const SharedMediaSelectionContext = createContext({})

let currentCategory: CategoryType = 'MEDIA'

const loadSharedMediaReporter = createUserWaitReporter(
  UserWaitPendingAction.LOAD_SHARED_MEDIA,
)
export function SharedMedia({ roomId }: { roomId: string }) {
  const theme = useTheme()
  const [fetching, setFetching] = useState(true)
  const [selectedCategory, setSelectedCategory] =
    useState<SharedMediaCategoryType>('MEDIA')

  const { items: openedRoomSharedMedia, hasMoreItemToLoad } =
    useOpenedRoomSharedMedia(roomId, selectedCategory)

  const {
    selectionMap,
    setSelection,
    resetSelection,
    selectedItems,
    hasSelection,
  } = useMultiSelectList<Message>({
    items: openedRoomSharedMedia,
    selectionPrimaryKey: 'iid',
  })

  const navigate = useNavigate()

  useHistoryCustomBack(() => {
    if (hasSelection) resetSelection()
    else navigate(-1)
  }, [hasSelection])

  const sharedMediaRef = useRef()

  const { showAudioPlayer } = useShowAudioPlayer()

  const handleCategoryChange = (newValue: CategoryType) => {
    openedRoomSharedMediaStorage.deleteAll()
    setSelectedCategory(newValue)
  }

  useEffect(() => {
    currentCategory = selectedCategory
    const fetchData = async () => {
      loadSharedMediaReporter.start()
      setFetching(true)
      showSharedMediaLoadingModal()

      await initiateSharedMediaContents(roomId, selectedCategory)
      await closeCurrentModal()
      setFetching(false)
      loadSharedMediaReporter.end({ selectedCategory, roomId })
    }
    fetchData()
  }, [selectedCategory])

  const room = useRoom(roomId)
  const roomType = room?.type

  const tabs = useMemo<ITab<SharedMediaCategoryType>[]>(() => {
    return CATEGORIES.map((category, index) => ({
      label: t(`sharedMedia.${category.toLowerCase()}`),
      value: category,
      ...a11yProps(index),
      width: `${100 / CATEGORIES.length}%`,
    }))
  }, [])

  return (
    <PageWithAppBar
      bodySx={{ backgroundColor: 'white' }}
      appBar={
        <AppBar
          title={t('profile.common.sharedMedia')}
          roomType={roomType}
          roomId={roomId}
          onBack={closeSharedMedia}
        />
      }
    >
      <Stack
        direction="column"
        ref={sharedMediaRef}
        sx={{ height: '100%', overflow: 'hidden', position: 'relative' }}
      >
        {hasSelection ? (
          <SelectionBar
            roomId={roomId}
            messages={selectedItems}
            onBack={resetSelection}
          />
        ) : (
          <Tabs
            value={selectedCategory}
            tabs={tabs}
            onChange={newValue => handleCategoryChange(newValue)}
            tabsSx={{ backgroundColor: 'white', marginTop: '5px' }}
            tabSx={{
              opacity: 1,
              "&[data-selected='true']": {
                color: theme.palette.neutral900,
              },
            }}
            indicatorStyles={{
              backgroundColor: theme.palette.neutral900,
            }}
          />
        )}

        {showAudioPlayer && <AudioPlayer position="static" hasLowerSibling />}

        {!fetching && !openedRoomSharedMedia?.length ? (
          <EmptyState />
        ) : (
          <SharedMediaSelectionContext.Provider value={selectionMap}>
            <MediaList
              sharedMediaRef={sharedMediaRef}
              key={selectedCategory}
              height={
                window.innerHeight -
                (APPBAR_HEIGHT +
                  TABS_HEIGHT +
                  (showAudioPlayer ? AUDIO_PLAYER_HEIGHT : 0))
              }
              items={openedRoomSharedMedia}
              columnCount={selectedCategory === 'MEDIA' ? 3 : 1}
              hasMoreItemToLoad={hasMoreItemToLoad}
              loadMore={() =>
                loadMoreSharedMediaContents(roomId, selectedCategory)
              }
              setSelection={setSelection}
              isOnLinkTab={selectedCategory === 'LINK'}
            />
          </SharedMediaSelectionContext.Provider>
        )}
      </Stack>
    </PageWithAppBar>
  )
}

export const sharedMediaApi = {
  getCategory() {
    return currentCategory
  },
}
